import React, {
  useState,
  useEffect,
  useRef,
  useImperativeHandle,
  useCallback,
  forwardRef,
} from 'react'
import ReactGa from 'react-ga'
import { useDispatch, useSelector } from 'react-redux'
import useMediaQuery from '@material-ui/core/useMediaQuery'
import { useTheme } from '@material-ui/core/styles'
import clsx from 'clsx'
import {
  Badge,
  Button,
  Divider,
  makeStyles,
  Grid,
  Checkbox,
  FormControlLabel,
} from '@material-ui/core'

import KeyboardArrowUpIcon from '@material-ui/icons/KeyboardArrowUp'
import KeyboardArrowDownIcon from '@material-ui/icons/KeyboardArrowDown'
import AddIcon from '@material-ui/icons/Add'
import CloseIcon from '@material-ui/icons/Close'

import * as actions from '../actionTypes'

const useStyles = makeStyles((theme) => ({
  activeMenu: {
    backgroundColor: '#fff !important', // override backgroundColor prop
    border: '1px solid #70707066',
    borderBottom: 'none',
    borderBottomColor: 'transparent',
    borderRadius: '4px 4px 0 0',
  },
  wrapper: {
    position: 'relative',
  },
  buttons: {
    width: '100%',
    height: 56,
    boxShadow: '0 2px 2px #70707066',
    display: 'flex',
    justifyContent: 'flex-start',
    padding: '0 14px',
    [theme.breakpoints.down('md')]: {
      height: 40,
    },
  },
  dropdownText: {
    color: '#1E2553',
    fontWeight: 700,
    textTransform: 'initial',
    textAlign: 'initial',
    fontSize: '1rem',
    flexGrow: 1,
  },
  buttonText: {
    fontWeight: 700,
    textTransform: 'uppercase',
    textAlign: 'center',
    fontSize: '0.75rem',
    flexGrow: 1,
  },
  arrowWrapper: {
    display: 'flex',
    flexDirection: 'column',
    height: 28,
    justifyContent: 'center',
  },
  arrows: {
    position: 'relative',
    flexGrow: 1,
  },
  arrowUp: {
    top: 5,
  },
  arrowDown: {
    bottom: 5,
  },
  dropdownMenu: {
    backgroundColor: 'white',
    boxShadow: '0 2px 2px  #70707066',
    border: '1px solid #70707066',
    borderTop: 'none',
    paddingTop: 30,
    position: 'relative',
    zIndex: 1,
  },
  buttonsWrapper: {
    width: '100%',
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'space-between',
  },
  selectAllButton: {
    border: '2px solid #1E2553',
    background: 'white',
    borderRadius: 3,
    flex: 1,
    justifyContent: 'space-between',
    height: 38,
    margin: '0 5px 0 10px',
    '& p': {
      whiteSpace: 'nowrap',
      margin: 0,
      padding: '2px 0',
    },
    fontSize: '0.8rem',
    fontWeight: '700',
    textTransform: 'uppercase',
    color: '#1E2553',
    padding: '5px 10px 5px 3px',
  },
  addCloseButtons: {
    display: 'flex',
    alignItems: 'center',
    flexShrink: 1,
  },
  filterButton: {
    height: 38,
    flex: 1,
    margin: '0 10px 0 5px',
    fontSize: '0.8rem',
    fontWeight: '700',
    '& p': {
      whiteSpace: 'nowrap',
      margin: 0,
      padding: '2px 0',
    },
  },
  divider: {
    margin: '20px 0',
  },
  checkBoxWrapper: {
    width: '100%',
    padding: '0 10px',
    display: 'flex',
    flexDirection: 'column',
    textAlign: 'left',
  },
}))

const Filter = ({ text, backgroundColor, icon, filterArray, filterName }, ref) => {
  const classes = useStyles()
  const dispatch = useDispatch()
  const [isHidden, setIsHidden] = useState(true)
  const [selectAllButton, setSelectAllButton] = useState(false)
  const { filters } = useSelector((state) => state.projectBoard)
  const { available, selected } = filters[filterName]
  const filterRef = useRef()
  const theme = useTheme()
  const matches = useMediaQuery(theme.breakpoints.down('md'))

  const handleClick = () => {
    setIsHidden(!isHidden)
  }

  useImperativeHandle(ref, () => ({
    close: function () {
      setIsHidden(true)
    },
    node: filterRef.current,
  }))

  const handleCheck = useCallback(
    (event) => {
      const filter = event.target.name
      ReactGa.event({
        category: "Filter Project ",
        action: filterName,
        label: filter
      })
      dispatch({
        type: actions.FILTER_SELECTION_REQUEST,
        payload: { category: filterName, filter },
      })
    },
    [dispatch, filterName]
  )

  const filterSelectionRequest = () => {
    ReactGa.event({
      category: "Search Project",
      action: "Click To Search"
    })
    if (matches) {
      dispatch({ type: actions.HIDE_FILTERS_MOBILE })
    }
    dispatch({
      type: actions.PROJECT_BOARD_ACTIVE_PROJECTS_REQUEST,
      payload: { pageNumber: 1, scrollToTop: true },
    })
    setIsHidden(!isHidden)
  }

  const selectAll = () => {
    setSelectAllButton(!selectAllButton)
    if (!selectAllButton) {
      dispatch({
        type: actions.FILTER_SELECTION_REQUEST_ALL,
        payload: { category: filterName },
      })
    } else {
      dispatch({
        type: actions.FILTER_DESELECTION_REQUEST_ALL,
        payload: { category: filterName },
      })
    }
  }

  useEffect(() => {
    if (available.length !== 0) {
      if (available.length === selected.length) {
        setSelectAllButton(true)
      } else {
        setSelectAllButton(false)
      }
    }
  }, [available, selected, selectAllButton])

  return (
    <div className={classes.wrapper} ref={filterRef}>
      <Button
        className={clsx(classes.buttons, !isHidden && classes.activeMenu)}
        startIcon={
          <Badge badgeContent={selected.length} color="secondary">
            {icon}
          </Badge>
        }
        style={{ backgroundColor }}
        onClick={handleClick}
      >
        <p className={classes.dropdownText}>{text}</p>
        <div className={classes.arrowWrapper}>
          <KeyboardArrowUpIcon className={clsx(classes.arrowUp, classes.arrows)} color="primary" />
          <KeyboardArrowDownIcon
            className={clsx(classes.arrowDown, classes.arrows)}
            color="primary"
          />
        </div>
      </Button>
      {!isHidden && (
        <div className={classes.dropdownMenu}>
          <div className={classes.buttonsWrapper}>
            <Button
              onClick={() => selectAll()}
              className={classes.selectAllButton}
              style={
                !selectAllButton
                  ? { backgroundColor: 'white' }
                  : { backgroundColor: '#009ECC', color: 'white', border: 'transparent' }
              }
            >
              <div className={classes.addCloseButtons}>
                {!selectAllButton ? <AddIcon /> : <CloseIcon />}
              </div>
              <p className={classes.buttonText}>Select All</p>
            </Button>
            <Button
              className={classes.filterButton}
              onClick={() => filterSelectionRequest()}
              color="primary"
              variant="contained"
            >
              <p className={classes.buttonText}>Filter by</p>
            </Button>
          </div>
          <Divider className={classes.divider} />
          <Grid container className={classes.checkBoxWrapper}>
            {filterArray.map((filter) => (
              <FormControlLabel
                key={filter}
                control={
                  <Checkbox
                    checked={selected.indexOf(filter) > -1}
                    onChange={(event) => handleCheck(event)}
                    name={filter}
                    color="primary"
                    value={filter}
                  />
                }
                label={filter}
              />
            ))}
          </Grid>
        </div>
      )}
    </div>
  )
}

export default forwardRef(Filter)
